import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoPicker } from "./PhotoPicker";
import { ChoixDate } from "./Echeance";
import { DEADLINE_MAX_JOURS, aujourdhui, lundiProchain, plusJours, raccourcisDeadline, joursAvant, relatif } from "@/lib/echeance";
import { api } from "@/lib/api";
import { envoyerPhoto } from "@/lib/photos";
import { useAuth } from "@/context/AuthContext";
import { peut } from "@/lib/permissions";

const AUCUN = "__aucun__";

export const instanceVide = () => ({ motif: null, probleme: null, detail: "", jour: "", commentaire: "", photos: [] });

/** Formulaire d'instance : menu 1 « Type d'instance » (motif principal :
 * Absent, Problème adresse, Cassé, RTS), menu 2 « Problème » (motif précis,
 * propre au type), puis la date : remise en trafic (Absent) ou dead line de
 * 5 jours (Problème adresse, Cassé, RTS). `motifs` = { types, problemes } (API). */
export function InstanceForm({ motifs, value, onChange, photos = true, compact = false }) {
  const v = value;
  const { user } = useAuth();
  photos = photos && peut(user, "colis_photos");
  const set = (patch) => onChange({ ...v, ...patch });
  const types = motifs?.types || [];
  const type = types.find((t) => t.id === v.motif);
  const problemes = (motifs?.problemes || []).filter((p) => p.type === v.motif);
  const typeDate = type?.date;
  const pbObligatoire = !!type?.probleme_obligatoire;

  const choisirType = (id) => {
    const t = types.find((x) => x.id === id);
    // Nouveau type : le problème est remis à zéro, la date est proposée
    // (remise demain pour Absent, dead line J+5 sinon).
    const patch = { motif: id, probleme: null, detail: "" };
    if (t?.date !== typeDate || !v.jour) patch.jour = t?.defaut_jours ? plusJours(t.defaut_jours) : "";
    set(patch);
  };
  const choisirProbleme = (x) => {
    const pb = problemes.find((p) => p.id === x);
    const patch = { probleme: x === AUCUN ? null : x };
    if (pb?.defaut_jours) patch.jour = plusJours(pb.defaut_jours);
    set(patch);
  };
  const raccourcis = typeDate === "limite"
    ? raccourcisDeadline()
    : [["Aujourd'hui", aujourdhui()], ["Demain", plusJours(1)], ["Après-demain", plusJours(2)], ["Lundi prochain", lundiProchain()]];
  const autre = !!v.probleme && v.probleme.endsWith("_autre");

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Type d'instance *</Label>
          <Select value={v.motif || undefined} onValueChange={choisirType}>
            <SelectTrigger data-testid="instance-type"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {types.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  <span className={t.rouge || t.id === "rts" ? "font-semibold text-red-600 dark:text-red-400" : ""}>{t.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Problème{pbObligatoire ? " *" : ""}</Label>
          <Select value={v.probleme || (pbObligatoire ? undefined : AUCUN)} onValueChange={choisirProbleme} disabled={!type}>
            <SelectTrigger data-testid="instance-probleme"><SelectValue placeholder={type ? "Choisir le motif précis…" : "Choisissez d'abord le type"} /></SelectTrigger>
            <SelectContent>
              {!pbObligatoire && <SelectItem value={AUCUN}>— Aucun —</SelectItem>}
              {problemes.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {autre && (
        <div className="space-y-1.5">
          <Label htmlFor="inst-detail">Précisez « Autre » *</Label>
          <Input id="inst-detail" value={v.detail} onChange={(e) => set({ detail: e.target.value })} maxLength={200} data-testid="instance-detail" />
        </div>
      )}
      {typeDate && (
        <div className={`space-y-1.5 rounded-lg border p-3 ${typeDate === "limite" ? "border-red-500/40 bg-red-500/5" : "border-sky-500/40 bg-sky-500/5"}`} data-testid="instance-date-bloc">
          <Label>{typeDate === "limite" ? "Date limite avant retour à l'expéditeur (dead line) *" : "Date de remise en trafic *"}</Label>
          <ChoixDate type={typeDate} value={v.jour} onChange={(jour) => set({ jour })} raccourcis={raccourcis} min={aujourdhui()} max={typeDate === "limite" ? plusJours(DEADLINE_MAX_JOURS) : undefined} testId="instance-date" />
          {typeDate === "limite" && v.jour && (
            <p className="text-sm font-bold text-red-600 dark:text-red-400" data-testid="instance-compte-rebours">
              ⏳ Dead line {relatif({ type: "limite", jours: joursAvant(v.jour) })} — sans solution, retour à l'expéditeur (ce n'est pas une remise en trafic).
            </p>
          )}
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="inst-comm">Commentaire</Label>
        <Textarea id="inst-comm" value={v.commentaire} onChange={(e) => set({ commentaire: e.target.value })} rows={compact ? 2 : 3} placeholder="ex. avis de passage déposé, voisin absent…" data-testid="instance-commentaire" />
      </div>
      {photos && (
        <div className="space-y-1.5">
          <Label>Photos</Label>
          <PhotoPicker value={v.photos} onChange={(p) => set({ photos: p })} />
        </div>
      )}
    </div>
  );
}

export const instanceValide = (motifs, v) => {
  const t = motifs?.types?.find((x) => x.id === v.motif);
  const pb = motifs?.problemes?.find((x) => x.id === v.probleme);
  return !!t && (!t.probleme_obligatoire || (!!pb && pb.type === t.id))
    && (!v.probleme?.endsWith("_autre") || v.detail.trim()) && !!v.jour && v.jour >= aujourdhui()
    && (t.date !== "limite" || v.jour <= plusJours(DEADLINE_MAX_JOURS));
};

/** Enregistre l'instance puis ses photos. Renvoie la fiche colis à jour. */
export async function enregistrerInstance(colisId, v, onProgress) {
  const { data } = await api.post(`/colis/${colisId}/instances`, {
    motif: v.motif, probleme: v.probleme, detail: v.detail, commentaire: v.commentaire, date: v.jour,
  });
  let fiche = data;
  const inst = fiche.instances[fiche.instances.length - 1];
  const erreurs = [];
  for (let i = 0; i < v.photos.length; i++) {
    onProgress?.(`${i + 1}/${v.photos.length}`);
    try { await envoyerPhoto(api, colisId, v.photos[i].file, { instanceId: inst.id }); } catch (e) { erreurs.push(e); }
  }
  if (v.photos.length) fiche = (await api.get(`/colis/${colisId}`)).data;
  return { fiche, inst, erreurs };
}
